import React, { useState, useEffect } from 'react';
import { getDeploymentHistory } from '../api/client';
import { User } from '../types';
import Navbar from '../components/Navbar';
import './DeploymentHistory.css';

interface DeploymentRecord {
  id: string;
  deployed_at: string;
  service: string;
  commit_sha: string;
  commit_message: string;
  author: string;
}

const DeploymentHistory: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [history, setHistory] = useState<DeploymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        const data = await getDeploymentHistory(page);
        setHistory(data.data || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      } catch (err) {
        console.error('Ошибка загрузки истории:', err);
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, [page]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const serviceLabels: Record<string, string> = {
    'karbi-workplace': 'Рабочее пространство',
    'karbi-workplane': 'Задачи',
    'karbi-auth': 'Аутентификация',
    'karbi-pay': 'Платежи',
    'api-gateway': 'API Gateway',
    'admin-panel': 'Админ-панель'
  };

  const getServiceLabel = (service: string) => serviceLabels[service] || service;

  const interpretChange = (message: string): string => {
    if (!message) return '';

    let text = message.replace(/^(feat|fix|chore|docs|style|refactor|test|perf|ci|build|merge)\s*:\s*/i, '').trim();

    const actionMap: Record<string, { past: string; present: string }> = {
      'add': { past: 'Добавлено', present: 'Добавляется' },
      'fix': { past: 'Исправлено', present: 'Исправляется' },
      'update': { past: 'Обновлено', present: 'Обновляется' },
      'remove': { past: 'Удалено', present: 'Удаляется' },
      'change': { past: 'Изменено', present: 'Изменяется' },
      'implement': { past: 'Реализовано', present: 'Реализуется' },
      'improve': { past: 'Улучшено', present: 'Улучшается' },
      'refactor': { past: 'Рефакторинг', present: 'Рефакторинг' },
      'delete': { past: 'Удалено', present: 'Удаляется' },
    };

    const subjectMap: Record<string, string> = {
      'deployment history page': 'страница истории обновлений',
      'deployment history': 'история обновлений',
      'notifications page': 'страница уведомлений',
      'notifications': 'уведомления',
      'notification': 'уведомление',
      'filters': 'фильтры задач',
      'filter': 'фильтр',
      'column settings': 'настройки колонок',
      'columns': 'колонки',
      'settings': 'настройки',
      'workplane': 'Задачи',
      'workplace': 'Рабочее пространство',
      'karbi_pay': 'Платежи',
      'karbi-pay': 'Платежи',
      'api-gateway': 'API Gateway',
      'admin panel': 'Админ-панель',
      'task': 'задача',
      'tasks': 'задачи',
      'comment': 'комментарий',
      'comments': 'комментарии',
      'user': 'пользователь',
      'users': 'пользователи',
      'page': 'страница',
      'button': 'кнопка',
      'modal': 'модальное окно',
      'form': 'форма',
      'table': 'таблица',
      'menu': 'меню',
      'navigation': 'навигация',
    };

    const prepositionMap: Record<string, string> = {
      'in ': 'в ',
      'on ': 'на ',
      'to ': 'на ',
      'for ': 'для ',
      'with ': 'с ',
      'and ': '. ',
      '&': '.',
    };

    let result = text;

    for (const [eng, rus] of Object.entries(prepositionMap)) {
      result = result.replace(new RegExp(eng, 'gi'), rus);
    }

    let actions: string[] = [];
    let currentAction = '';

    const parts = result.split(/\s+/);
    for (const part of parts) {
      const lowerPart = part.toLowerCase().replace(/[^a-z]/g, '');

      if (actionMap[lowerPart]) {
        if (currentAction) {
          actions.push(currentAction.trim());
        }
        currentAction = actionMap[lowerPart].past + ' ';
      } else if (currentAction) {
        currentAction += part + ' ';
      } else {
        currentAction = part + ' ';
      }
    }
    if (currentAction) {
      actions.push(currentAction.trim());
    }

    if (actions.length === 0) {
      actions = [result];
    }

    let finalResult = actions.map(action => {
      let sentence = action;
      for (const [eng, rus] of Object.entries(subjectMap)) {
        sentence = sentence.replace(new RegExp(eng, 'gi'), rus);
      }

      if (!sentence.match(/[а-яё]/i)) {
        sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1);
      }

      if (!/[.!?]$/.test(sentence)) {
        sentence += '.';
      }

      return sentence;
    }).join(' ');

    return finalResult || message;
  };

  if (loading && history.length === 0) {
    return (
      <div className="workplace-container">
        {user && <Navbar user={user} />}
        <div className="workplace-loading">
          <div className="spinner"></div>
          <p>Загрузка истории...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workplace-container">
      {user && <Navbar user={user} />}
      <div className="changelog-page">
        <div className="changelog-header">
          <h1>История обновлений</h1>
          <p className="changelog-subtitle">Последние изменения в системе</p>
        </div>

        {history.length === 0 ? (
          <div className="changelog-empty">
            <p>История обновлений пуста</p>
          </div>
        ) : (
          <>
            <div className="changelog-table-wrapper">
              <table className="changelog-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Сервис</th>
                    <th>Что нового</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((record) => (
                    <tr key={record.id}>
                      <td className="changelog-date">{formatDate(record.deployed_at)}</td>
                      <td className="changelog-service">
                        <span className="service-badge">{getServiceLabel(record.service)}</span>
                      </td>
                      <td className="changelog-message">
                        <span>{interpretChange(record.commit_message)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="changelog-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                >
                  ← Назад
                </button>
                <span className="pagination-info">
                  Страница {page} из {pages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= pages}
                >
                  Вперёд →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeploymentHistory;